import { useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../shared/ui/button/Button";
import { Coupon } from "../../domain/types";

interface CouponFieldProps {
    /** Checks the code with the provider. Rejects when it is not a live discount. */
    onCheck: (code: string) => Promise<Coupon>;
    /** The code that will travel with the purchase, or undefined for none. */
    onApplied: (coupon: Coupon | null) => void;
    applied: Coupon | null;
    disabled?: boolean;
}

/**
 * A discount code, typed before paying.
 *
 * Collapsed until somebody asks for it: most people have no code, and a field
 * shouting "you could be paying less" on every purchase screen is a reason to
 * abandon it and go looking for one.
 *
 * **It never restates the total.** What a discount comes to on a particular
 * invoice depends on rules that live with the money — which prices it covers,
 * for how many invoices, with what minimum — so the screen shows what the
 * provider says the coupon does and the amount is applied where it is charged.
 * A total worked out here would eventually disagree with the card statement.
 */
export default function CouponField({
    onCheck,
    onApplied,
    applied,
    disabled = false,
}: CouponFieldProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [code, setCode] = useState("");
    const [checking, setChecking] = useState(false);
    const [failed, setFailed] = useState(false);

    const apply = async () => {
        const typed = code.trim();
        if (!typed) return;
        setChecking(true);
        setFailed(false);
        try {
            onApplied(await onCheck(typed));
        } catch {
            setFailed(true);
            onApplied(null);
        } finally {
            setChecking(false);
        }
    };

    if (applied) {
        return (
            <div className="flex flex-wrap items-center gap-3 rounded-lg bg-green-50 p-3 dark:bg-green-500/10">
                <span className="text-sm text-green-800 dark:text-green-300">
                    {t("subscription.coupon.applied", {
                        code: applied.code,
                        describes: applied.describes,
                    })}
                </span>
                <button
                    type="button"
                    className="ml-auto text-sm font-medium text-content-secondary underline"
                    onClick={() => {
                        onApplied(null);
                        setCode("");
                        setOpen(true);
                    }}
                >
                    {t("subscription.coupon.remove")}
                </button>
            </div>
        );
    }

    if (!open) {
        // Wrapped, because this is the one state whose root would otherwise be
        // an inline element: a bare button sits on the same line as whatever
        // follows it, and what follows it here is the button that pays.
        return (
            <div>
                <button
                    type="button"
                    className="text-sm font-medium text-content-secondary underline"
                    onClick={() => setOpen(true)}
                >
                    {t("subscription.coupon.have")}
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-end gap-2">
                <label className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-content-primary">
                        {t("subscription.coupon.label")}
                    </span>
                    <input
                        type="text"
                        value={code}
                        maxLength={64}
                        autoComplete="off"
                        aria-invalid={failed}
                        onChange={(event) => {
                            setCode(event.target.value);
                            setFailed(false);
                        }}
                        className="w-56 rounded-lg border border-gray-300 px-3 py-2 text-sm uppercase dark:border-gray-600 dark:bg-gray-900"
                    />
                </label>
                <Button
                    variant="outline"
                    onClick={() => void apply()}
                    disabled={disabled || checking || code.trim().length === 0}
                >
                    {t("subscription.coupon.apply")}
                </Button>
            </div>
            {failed && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                    {t("subscription.coupon.invalid")}
                </p>
            )}
            {/* Said before it is pressed: checking the code is what moves the
                total, not typing it. */}
            <p className="text-xs text-content-tertiary">
                {t("subscription.coupon.appliedOnCheck")}
            </p>
        </div>
    );
}
